import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import '../styles/responsive/pages/Settings.css';
// After the responsive sheet on purpose: the rail overrides in here need to
// outrank its .settings-rail-item.active rules at equal specificity.
import '../styles/pages/settings.css';
import useCountUp from '../hooks/useCountUp';
import { logout, updateUser } from "../store/slices/authSlice";
import { ethers } from "ethers";
import {
  Building2,
  Wallet,
  User,
  Save,
  Upload,
  Shield,
  AlertCircle,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  Settings as SettingsIcon,
  Coins,
  Copy,
  Check,
  Bell,
  ChevronRight,
  ChevronLeft,
  Circle
} from "lucide-react";
import { useIsMobile } from "../hooks/useMediaQuery";
import PayoutSettingsTab from "../components/settings/PayoutSettingsTab";
import SecurityTab from "../components/settings/SecurityTab";
import NotificationsTab from "../components/settings/NotificationsTab";
import PhoneInput from "../components/common/PhoneInput";
import { splitPhone, findCountry } from "../components/common/countryDialCodes";
import { fetchWithAuth } from "../utils/api";
import { hasHtmlTag, HTML_TAG_MESSAGE } from "../utils/sanitizeText";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const isValidEthereumAddress = (address) => {
  if (!address) return true;
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

// const checkIfContractAddress = async (address) => {
//   if (!address || !isValidEthereumAddress(address)) {
//     return { isContract: false, error: "Invalid address" };
//   }

//   try {
//     const provider = new ethers.JsonRpcProvider(
//       process.env.REACT_APP_DEFAULT_RPC_URL || "https://eth.llamarpc.com",
//     );

//     const code = await provider.getCode(address);

//     const isContract = code !== "0x" && code !== "0x0";

//     return { isContract, error: null };
//   } catch (error) {
//     console.error("Error checking contract address:", error);
//     return {
//       isContract: false,
//       error: "Unable to verify address type. Please try again.",
//     };
//   }
// };

const checkIfContractAddress = async (address) => {
  if (!address || !isValidEthereumAddress(address)) {
    return { isContract: false, error: "Invalid address" };
  }

  const RPC_URLS = [
    process.env.REACT_APP_DEFAULT_RPC_URL,
    "https://eth.llamarpc.com",
    "https://cloudflare-eth.com",
    "https://rpc.ankr.com/eth",
    "https://ethereum.publicnode.com",
  ].filter(Boolean);

  for (const rpcUrl of RPC_URLS) {
    try {
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      provider.pollingInterval = 4000;

      const code = await Promise.race([
        provider.getCode(address),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout")), 5000)
        ),
      ]);

      const isContract = code !== "0x" && code !== "0x0";
      return { isContract, error: null };
    } catch {
      continue; 
    }
  }

  return { isContract: false, error: "All RPC endpoints failed. Please try again." };
};

// Supported chains for custom-token resolution. Resolution happens on the
// frontend (read decimals()/name()/symbol() on-chain) to avoid backend SSRF.
const SUPPORTED_CHAIN_RPCS = {
  1: ["https://eth.llamarpc.com", "https://cloudflare-eth.com", "https://ethereum.publicnode.com"],
  56: ["https://bsc-dataseed.binance.org", "https://bsc.publicnode.com"],
  137: ["https://polygon-rpc.com", "https://polygon-bor.publicnode.com"],
  42161: ["https://arb1.arbitrum.io/rpc", "https://arbitrum-one.publicnode.com"],
  10: ["https://mainnet.optimism.io", "https://optimism.publicnode.com"],
  8453: ["https://mainnet.base.org", "https://base.publicnode.com"],
};

const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
];

// Read ERC-20 metadata for a token on a supported chain. Tries each RPC for the
// chain until one responds. Returns { error } if the chain is unsupported or all
// RPCs fail.
const resolveErc20Metadata = async (address, chainId) => {
  const rpcUrls = SUPPORTED_CHAIN_RPCS[Number(chainId)];
  if (!rpcUrls) {
    return { error: "Chain not supported. Supported chains: 1, 56, 137, 42161, 10, 8453." };
  }

  for (const rpcUrl of rpcUrls) {
    try {
      const provider = new ethers.JsonRpcProvider(rpcUrl);
      const contract = new ethers.Contract(address, ERC20_ABI, provider);
      const [decimals, name, symbol] = await Promise.race([
        Promise.all([contract.decimals(), contract.name(), contract.symbol()]),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 6000)),
      ]);
      return { decimals: Number(decimals), name, symbol, error: null };
    } catch {
      continue;
    }
  }

  return { error: "Could not read token details. Check the address and chain ID." };
};


const COUNTRY_PHONE_CONFIG = {
  US: {
    code: "+1",
    digits: 10,
    pattern: /^\+1[1-9]\d{9}$/,
    placeholder: "+1 (555) 000-0000",
    label: "United States",
  },
  UK: {
    code: "+44",
    digits: 10,
    pattern: /^\+44[1-9]\d{9}$/,
    placeholder: "+44 7911 123456",
    label: "United Kingdom",
  },
  CA: {
    code: "+1",
    digits: 10,
    pattern: /^\+1[1-9]\d{9}$/,
    placeholder: "+1 (555) 000-0000",
    label: "Canada",
  },
  AU: {
    code: "+61",
    digits: 9,
    pattern: /^\+61[1-9]\d{8}$/,
    placeholder: "+61 4XX XXX XXX",
    label: "Australia",
  },
  DE: {
    code: "+49",
    digits: 11,
    pattern: /^\+49[1-9]\d{10}$/,
    placeholder: "+49 1XX XXXXXXXX",
    label: "Germany",
  },
  FR: {
    code: "+33",
    digits: 9,
    pattern: /^\+33[1-9]\d{8}$/,
    placeholder: "+33 6 XX XX XX XX",
    label: "France",
  },
  IN: {
    code: "+91",
    digits: 10,
    pattern: /^\+91[1-9]\d{9}$/,
    placeholder: "+91 XXXXX XXXXX",
    label: "India",
  },
  JP: {
    code: "+81",
    digits: 10,
    pattern: /^\+81[1-9]\d{9}$/,
    placeholder: "+81 XX XXXX XXXX",
    label: "Japan",
  },
  BR: {
    code: "+55",
    digits: 11,
    pattern: /^\+55[1-9]\d{10}$/,
    placeholder: "+55 XX XXXXX XXXX",
    label: "Brazil",
  },
  MX: {
    code: "+52",
    digits: 10,
    pattern: /^\+52[1-9]\d{9}$/,
    placeholder: "+52 XX XXXX XXXX",
    label: "Mexico",
  },
};

// The country <select> and the phone picker speak different alphabets: the
// select keys the United Kingdom "UK", ISO-3166 keys it "GB". Everything else
// matches. Countries the select does not offer map to nothing, and the select
// is then left alone rather than snapped to a wrong value.
const ISO_TO_BUSINESS_COUNTRY = { GB: 'UK' };

const businessCountryForIso = (iso) => {
  const key = ISO_TO_BUSINESS_COUNTRY[iso] || iso;
  return COUNTRY_PHONE_CONFIG[key] ? key : null;
};

// ─── Profile phone number ───────────────────────────────────────────────────
// The phone must be an international number: a leading "+", the country code,
// then the subscriber number. E.164 caps the total at 15 digits and no real
// number is shorter than 7.
const PROFILE_PHONE_MAX_LENGTH = 20;
const PROFILE_PHONE_MIN_DIGITS = 7;
const PROFILE_PHONE_MAX_DIGITS = 15;

/**
 * A country picked with no number typed leaves a bare "+91". That is not a
 * phone number, so it is stored as empty rather than as a dangling dial code.
 */
const normalizeProfilePhone = (phone) => {
  const value = String(phone ?? '').trim();
  if (!value) return '';
  return splitPhone(value).national ? value.slice(0, PROFILE_PHONE_MAX_LENGTH) : '';
};

/** null when valid, otherwise the message to show under the field. */
const validateProfilePhone = (phone) => {
  const value = String(phone ?? '').trim();
  if (!value) return null; // optional field

  // Country chosen, nothing typed yet — still "no phone number given".
  if (!splitPhone(value).national) return null;

  if (!value.startsWith('+')) {
    return 'Start with the country code, for example +1 555 000 0000';
  }

  const digits = value.replace(/\D/g, '');
  if (digits.length < PROFILE_PHONE_MIN_DIGITS || digits.length > PROFILE_PHONE_MAX_DIGITS) {
    return `Enter ${PROFILE_PHONE_MIN_DIGITS} to ${PROFILE_PHONE_MAX_DIGITS} digits including the country code`;
  }

  return null;
};

// Tab ids accepted from the ?tab= deep link. Mirrors the ids in tabGroups, but
// kept at module scope so the sync effect does not have to depend on tabGroups,
// which is rebuilt on every render. The admin-only id is listed separately
// because it must additionally be gated on the role -- otherwise a hand-typed
// ?tab=payout would select a pane that renders nothing.
const SETTINGS_TAB_IDS = [
  "profile",
  "business",
  "security",
  "notifications",
  "wallet",
  "Token",
];
const SETTINGS_ADMIN_TAB_IDS = ["payout"];

const initialsOf = (first, last, email) => {
  const a = String(first || "").trim();
  const b = String(last || "").trim();
  if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase() || "?";
  const e = String(email || "").trim();
  return e ? e.charAt(0).toUpperCase() : "?";
};

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token } = useSelector((state) => state.auth);
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  // Seeded from the URL so a deep link lands on the right tab on first paint
  // rather than showing Profile for a frame and then switching. Validated here
  // too: the value is user-supplied.
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get("tab");
    return SETTINGS_TAB_IDS.includes(requested) ? requested : "profile";
  });
  // Mobile drill-in: false = show the iOS-style list, true = show the open
  // section's form full-screen with a back control. Ignored on desktop.
  const [mobileSectionOpen, setMobileSectionOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [errors, setErrors] = useState({});
  const [tokenErrors, setTokenErrors] = useState({});
  const [isSaved, setIsSaved] = useState(false);
  const [isSavingCustomToken, setIsSavingCustomToken] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(null);

  const handleCopyAddress = (key, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedAddress(key);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const [customToken, setCustomToken] = useState({
    contractAddress: "",
    chainId: "",
    decimals: "",
    notes: "",
  });
  // Read-only display of on-chain name/symbol once resolved (model has no columns).
  const [resolvedTokenMeta, setResolvedTokenMeta] = useState({ name: "", symbol: "" });
  const [isResolvingToken, setIsResolvingToken] = useState(false);

  // Auto-detect decimals (and name/symbol) from the token contract as soon as a
  // well-formed address + positive chain ID are present, so the Decimals field
  // shows the contract's real value before the merchant submits.
  useEffect(() => {
    const addr = customToken.contractAddress?.trim();
    const cid = customToken.chainId;
    const addrOk = addr && /^0x[a-fA-F0-9]{40}$/.test(addr);
    const cidOk = cid && Number.isInteger(Number(cid)) && Number(cid) > 0;
    if (!addrOk || !cidOk) return;

    let cancelled = false;
    setIsResolvingToken(true);
    const timer = setTimeout(async () => {
      const meta = await resolveErc20Metadata(addr, cid);
      if (cancelled) return;
      setIsResolvingToken(false);
      if (meta.error) {
        setTokenErrors((prev) => ({ ...prev, contractAddress: meta.error }));
        setCustomToken((prev) => ({ ...prev, decimals: "" }));
        setResolvedTokenMeta({ name: "", symbol: "" });
      } else {
        setTokenErrors((prev) => ({ ...prev, contractAddress: "" }));
        setCustomToken((prev) => ({ ...prev, decimals: String(meta.decimals) }));
        setResolvedTokenMeta({ name: meta.name, symbol: meta.symbol });
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customToken.contractAddress, customToken.chainId]);

  const handleSaveCustomToken = async () => {
    const newErrors = {};

    if (!customToken.contractAddress?.trim()) {
      newErrors.contractAddress = "Contract address is required";
    } else if (!/^0x[a-fA-F0-9]{40}$/.test(customToken.contractAddress)) {
      newErrors.contractAddress = "Invalid Ethereum address format";
    }
    if (!customToken.chainId) {
      newErrors.chainId = "Chain ID is required";
    } else if (!Number.isInteger(Number(customToken.chainId)) || Number(customToken.chainId) <= 0) {
      newErrors.chainId = "Chain ID must be a positive number";
    }

    setTokenErrors(newErrors);
    if (Object.keys(newErrors).length) {
      return;
    }

    setIsSavingCustomToken(true);
    setMessage({ type: "", text: "" });

    // Decimals come from the token contract (auto-detected). If detection hasn't
    // completed yet (merchant submitted early), resolve now as a fallback.
    let decimals = customToken.decimals;
    if (decimals === "" || decimals === null || decimals === undefined) {
      const meta = await resolveErc20Metadata(
        customToken.contractAddress,
        customToken.chainId,
      );
      if (meta.error) {
        setTokenErrors((prev) => ({ ...prev, contractAddress: meta.error }));
        setIsSavingCustomToken(false);
        return;
      }
      decimals = meta.decimals;
      setCustomToken((prev) => ({ ...prev, decimals: String(meta.decimals) }));
      setResolvedTokenMeta({ name: meta.name, symbol: meta.symbol });
    }

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/merchants/custom-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            contractAddress: customToken.contractAddress,
            chainId: customToken.chainId.toString(),
            decimals: Number(decimals),
          }),
        },
      );

      const data = await response.json();
      if (!response.ok) throw new Error("Failed to save token");

      // Success
      setIsSaved(true);

      if (data.success) {
        setMessage({
          type: "success",
          text: "Custom token request submitted successfully!",
        });
        setCustomToken({
          contractAddress: "",
          chainId: "",
          decimals: "",
          notes: "",
        });
        setResolvedTokenMeta({ name: "", symbol: "" });
        setTokenErrors({});
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to submit token request",
        });
      }
    } catch (error) {
      console.error("Error submitting custom token request:", error);
      setMessage({ type: "error", text: "Failed to submit token request" });
    } finally {
      setIsSavingCustomToken(false);
    }
  };

  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    businessType: 'technology',
    website: '',
    description: '',
    country: 'US',
    address: '',
    phone: '',
  });

  const [logoUrl, setLogoUrl] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // Picking a file used to POST it straight away, so the logo was live on
  // invoices before the merchant had seen it next to the rest of the form and
  // before they pressed Save. Both the new file and a pending removal are now
  // held here and applied by handleSaveBusinessInfo, so the whole panel commits
  // as one action.
  //
  // pendingLogoFile  -- a File chosen but not yet uploaded
  // pendingLogoPreview -- its object URL, revoked when it is replaced or dropped
  // pendingLogoRemoval -- the existing logo is to be deleted on save
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [pendingLogoPreview, setPendingLogoPreview] = useState('');
  const [pendingLogoRemoval, setPendingLogoRemoval] = useState(false);

  const [walletInfo, setWalletInfo] = useState({
    ethAddress: '',
  });

  const [isCheckingAddress, setIsCheckingAddress] = useState(false);

  const [pendingWalletRequest, setPendingWalletRequest] = useState(null);
  const [walletChangeReason, setWalletChangeReason] = useState('');
  const [originalWalletAddress, setOriginalWalletAddress] = useState('');

  const [originalBusinessInfo, setOriginalBusinessInfo] = useState(null);
  const [originalProfileInfo, setOriginalProfileInfo] = useState(null);

  const [gasTankInfo, setGasTankInfo] = useState({
    gasTankAddress: '',
    mainAddress: '',
  });

  const [profileInfo, setProfileInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [passwordInfo, setPasswordInfo] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/user-details`);
        const data = await response.json();

        if (data.success && data.data) {
          const { businessInfo: bInfo, profileInfo: pInfo } = data.data;
          if (bInfo) {
            const businessData = {
              businessName: bInfo.businessName || '',
              businessType: bInfo.businessType || 'technology',
              website: bInfo.website || '',
              description: bInfo.description || '',
              country: bInfo.country || 'US',
              address: bInfo.address || '',
              phone: bInfo.phone || '',
            };
            setBusinessInfo(businessData);
            setOriginalBusinessInfo(businessData);
            setWalletInfo({
              ethAddress: bInfo.ethereumAddress || '',
            });
            setOriginalWalletAddress(bInfo.ethereumAddress || '');
            if (bInfo.logoUrl) {
              setLogoUrl(bInfo.logoUrl);
            }
          }

          if (data.pendingWalletChangeRequest) {
            setPendingWalletRequest(data.pendingWalletChangeRequest);
          }
          if (pInfo) {
            const profileData = {
              firstName: pInfo.firstName || user?.firstName || user?.first_name || '',
              lastName: pInfo.lastName || user?.lastName || user?.last_name || '',
              email: pInfo.email || user?.email || '',
              phone: pInfo.phone || '',
            };
            setProfileInfo(profileData);
            setOriginalProfileInfo(profileData);
          } else {
            const profileData = {
              firstName: user?.firstName || user?.first_name || '',
              lastName: user?.lastName || user?.last_name || '',
              email: user?.email || '',
              phone: '',
            };
            setProfileInfo(profileData);
            setOriginalProfileInfo(profileData);
          }
        } else {
          const profileData = {
            firstName: user?.firstName || user?.first_name || '',
            lastName: user?.lastName || user?.last_name || '',
            email: user?.email || '',
            phone: '',
          };
          setProfileInfo(profileData);
          setOriginalProfileInfo(profileData);
        }
      } catch (error) {
        console.error('Failed to fetch user details:', error);
        const profileData = {
          firstName: user?.firstName || user?.first_name || '',
          lastName: user?.lastName || user?.last_name || '',
          email: user?.email || '',
          phone: '',
        };
        setProfileInfo(profileData);
        setOriginalProfileInfo(profileData);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchUserDetails();
    } else {
      const profileData = {
        firstName: user?.firstName || user?.first_name || '',
        lastName: user?.lastName || user?.last_name || '',
        email: user?.email || '',
        phone: '',
      };
      setProfileInfo(profileData);
      setOriginalProfileInfo(profileData);
      setIsLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    const fetchGasTankAddress = async () => {
      if (!isSuperAdmin || !token) return;

      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/wallets/gas-tank-address?networkType=EVM`);
        const data = await response.json();

        if (data.success && data.data) {
          setGasTankInfo({
            gasTankAddress: data.data.gasTankAddress || '',
            mainAddress: data.data.mainAddress || '',
          });
        }
      } catch (error) {
        console.error('Failed to fetch gas tank address:', error);
      }
    };

    fetchGasTankAddress();
  }, [isSuperAdmin, token]);

  const isEqual = (obj1, obj2) => {
    if (!obj1 || !obj2) return false;
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  };

  // A staged logo counts as a change: without this, picking a file and pressing
  // Save would short-circuit on "No changes to save" and silently drop it.
  const hasPendingLogoChange = () => Boolean(pendingLogoFile) || pendingLogoRemoval;

  const hasBusinessInfoChanged = () => {
    if (hasPendingLogoChange()) return true;
    if (!originalBusinessInfo) return true;
    return !isEqual(businessInfo, originalBusinessInfo);
  };

  const hasProfileInfoChanged = () => {
    if (!originalProfileInfo) return true;
    return !isEqual(profileInfo, originalProfileInfo);
  };

  const hasWalletInfoChanged = () => {
    return walletInfo.ethAddress !== originalWalletAddress;
  };

  const validateBusinessInfo = () => {
    const newErrors = {};

    // Limits mirror the user_details columns (business_name and website are
    // VARCHAR(255), address VARCHAR(500)); description is TEXT, capped here so
    // it stays a description rather than a document.
    const name = businessInfo.businessName?.trim() || "";
    if (!name) {
      newErrors.businessName = "Business name is required";
    } else if (name.length > 255) {
      newErrors.businessName = "Business name must be 255 characters or fewer";
    } else if (hasHtmlTag(name)) {
      newErrors.businessName = `Business name ${HTML_TAG_MESSAGE}`;
    }

    // Every business field is required.
    if (!businessInfo.businessType) {
      newErrors.businessType = "Business type is required";
    }

    const website = businessInfo.website?.trim() || "";
    if (!website) {
      newErrors.website = "Website is required";
    } else {
      // The old check accepted "http://x" -- anything after the scheme. A real
      // address needs a host with a dot in it.
      let validUrl = false;
      try {
        const u = new URL(website);
        validUrl =
          (u.protocol === "http:" || u.protocol === "https:") &&
          /\.[a-z]{2,}$/i.test(u.hostname);
      } catch {
        validUrl = false;
      }
      if (!validUrl) {
        newErrors.website =
          "Please enter a valid URL starting with http:// or https://";
      } else if (website.length > 255) {
        newErrors.website = "Website must be 255 characters or fewer";
      }
    }

    const description = (businessInfo.description || "").trim();
    if (!description) {
      newErrors.description = "Business description is required";
    } else if (description.length > 1000) {
      newErrors.description =
        "Business description must be 1000 characters or fewer";
    } else if (hasHtmlTag(description)) {
      newErrors.description = `Business description ${HTML_TAG_MESSAGE}`;
    }

    if (!businessInfo.country) {
      newErrors.country = "Country is required";
    }

    const address = (businessInfo.address || "").trim();
    if (!address) {
      newErrors.address = "Business address is required";
    } else if (address.length > 500) {
      newErrors.address = "Business address must be 500 characters or fewer";
    } else if (hasHtmlTag(address)) {
      newErrors.address = `Business address ${HTML_TAG_MESSAGE}`;
    }

    // Required here, unlike the profile phone. validateProfilePhone treats a
    // dial code with no number ("+91") as "not given", so that case is caught
    // first; the shared rule then checks the format. It no longer requires the
    // dial code to match the BUSINESS country: a US-registered company may
    // perfectly well list an Indian number.
    if (!splitPhone(String(businessInfo.phone ?? "").trim()).national) {
      newErrors.phone = "Phone number is required";
    } else {
      const phoneError = validateProfilePhone(businessInfo.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateWalletInfo = async () => {
    const newErrors = {};

    if (
      walletInfo.ethAddress &&
      !isValidEthereumAddress(walletInfo.ethAddress)
    ) {
      newErrors.ethAddress =
        "Invalid Ethereum address format. Must start with 0x followed by 40 hexadecimal characters";
      setErrors(newErrors);
      return false;
    }

    if (
      walletInfo.ethAddress &&
      walletInfo.ethAddress !== originalWalletAddress
    ) {
      setIsCheckingAddress(true);
      const { isContract, error } = await checkIfContractAddress(
        walletInfo.ethAddress,
      );
      setIsCheckingAddress(false);

      if (error) {
        newErrors.ethAddress = error;
      } else if (isContract) {
        newErrors.ethAddress =
          "Contract addresses are not allowed. Please use an Externally Owned Account (EOA) address only.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateProfileInfo = () => {
    const newErrors = {};
    const nameRegex = /^[\p{L}][\p{L}\s'.-]*$/u;

    if (!profileInfo.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    } else if (!nameRegex.test(profileInfo.firstName.trim())) {
      newErrors.firstName = "Please enter a valid first name";
    }

    if (!profileInfo.lastName?.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (!nameRegex.test(profileInfo.lastName.trim())) {
      newErrors.lastName = "Please enter a valid last name";
    }

    const phoneError = validateProfilePhone(profileInfo.phone);
    if (phoneError) {
      newErrors.phone = phoneError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordChange = () => {
    const newErrors = {};

    if (!passwordInfo.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!passwordInfo.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (passwordInfo.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordInfo.newPassword)
    ) {
      newErrors.newPassword =
        "Password must contain uppercase, lowercase, and number";
    }

    if (!passwordInfo.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (passwordInfo.newPassword !== passwordInfo.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Update one business field and drop its error, so the red message goes away
  // as soon as the merchant starts correcting it rather than on the next save.
  const updateBusinessField = (field, value) => {
    setBusinessInfo((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _dropped, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSaveBusinessInfo = async () => {
    // Validate first. With the change check first, pressing Save on an empty,
    // untouched form only said "No changes to save" and never showed which
    // required fields were missing.
    if (!validateBusinessInfo()) return;

    if (!hasBusinessInfoChanged()) {
      setMessage({ type: "info", text: "No changes to save" });
      return;
    }

    setIsSaving(true);
    setMessage({ type: "", text: "" });

    try {
      // The logo goes first: it is a separate endpoint, and if it is refused
      // there is no point reporting the panel as saved. commitPendingLogo sets
      // its own error message.
      const logoOk = await commitPendingLogo();
      if (!logoOk) return;

      // Nothing but a logo change — the text fields are untouched, so there is
      // no business PUT to make.
      if (isEqual(businessInfo, originalBusinessInfo)) {
        setMessage({ type: "success", text: "Business logo saved successfully!" });
        return;
      }

      const response = await fetchWithAuth(
        `${API_BASE_URL}/user-details/business`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          // Same normalisation as the profile phone: a country picked with no
          // number typed leaves a bare "+91", which is a dangling dial code
          // rather than a phone number and should be stored as empty.
          body: JSON.stringify({
            ...businessInfo,
            phone: normalizeProfilePhone(businessInfo.phone),
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: "success",
          text: "Business information saved successfully!",
        });
        setOriginalBusinessInfo({ ...businessInfo });
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to save business information",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Failed to save business information",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveWalletInfo = async () => {
    if (!hasWalletInfoChanged()) {
      setMessage({ type: "info", text: "No changes to save" });
      return;
    }

    const isValid = await validateWalletInfo();
    if (!isValid) return;

    setIsSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/user-details/ethereum-address`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ethereumAddress: walletInfo.ethAddress,
            reason: walletChangeReason || undefined,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        if (data.requiresApproval) {
          setMessage({
            type: "success",
            text:
              data.message ||
              "Wallet change request submitted for admin approval!",
          });
          setPendingWalletRequest(data.data);
          setWalletChangeReason("");
        } else {
          setMessage({
            type: "success",
            text: "Ethereum address saved successfully!",
          });
          setOriginalWalletAddress(walletInfo.ethAddress);
          setBusinessInfo((prev) => ({
            ...prev,
            ethereumAddress: walletInfo.ethAddress,
          }));
        }
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to save Ethereum address",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to save Ethereum address" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelWalletRequest = async () => {
    if (!pendingWalletRequest) return;

    setIsSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/user-details/wallet-change-requests/${pendingWalletRequest.requestId}/cancel`,
        {
          method: "POST",
        },
      );

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: "success",
          text: "Wallet change request cancelled successfully!",
        });
        setPendingWalletRequest(null);
        setWalletInfo({ ethAddress: originalWalletAddress });
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to cancel request",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to cancel request" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfileInfo = async () => {
    if (!hasProfileInfoChanged()) {
      setMessage({ type: "info", text: "No changes to save" });
      return;
    }

    if (!validateProfileInfo()) return;

    setIsSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/user-details/profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...profileInfo,
            phone: normalizeProfilePhone(profileInfo.phone),
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: "success",
          text: "Profile information saved successfully!",
        });
        setOriginalProfileInfo({ ...profileInfo });
        // Reflect the new name across the app (header, etc.) immediately.
        dispatch(
          updateUser({
            firstName: profileInfo.firstName,
            lastName: profileInfo.lastName,
            first_name: profileInfo.firstName,
            last_name: profileInfo.lastName,
          }),
        );
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to save profile information",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to save profile information" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!validatePasswordChange()) return;

    setIsSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/auth/change-password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            currentPassword: passwordInfo.currentPassword,
            newPassword: passwordInfo.newPassword,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: "success",
          text: "Password changed successfully! Redirecting to login...",
        });
        setPasswordInfo({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        setTimeout(() => {
          dispatch(logout());
          navigate("/login");
        }, 2000);
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to change password",
        });
      }
    } catch (error) {
      setMessage({ type: "error", text: "Failed to change password" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (activeTab === "business") {
      await handleSaveBusinessInfo();
    } else if (activeTab === "wallet") {
      await handleSaveWalletInfo();
    } else if (activeTab === "profile") {
      await handleSaveProfileInfo();
    } else if (activeTab === "Token") {
      await handleSaveCustomToken();
    }

  };

  // Drop a staged file and release its object URL. Object URLs hold the blob in
  // memory until revoked, and this panel can be cycled through many files in a
  // session.
  const clearPendingLogo = () => {
    setPendingLogoPreview("");
    setPendingLogoFile(null);
  };

  // An object URL holds its blob in memory until revoked, and this panel can be
  // cycled through several files in one sitting. Cleanup runs both when the URL
  // is replaced and when the page unmounts.
  useEffect(() => {
    if (!pendingLogoPreview) return undefined;
    return () => URL.revokeObjectURL(pendingLogoPreview);
  }, [pendingLogoPreview]);

  // Stage only. The file is validated and previewed here; the upload itself
  // happens in handleSaveBusinessInfo so nothing reaches the server until the
  // merchant presses Save.
  const handleLogoSelect = (event) => {
    const file = event.target.files?.[0];
    // Reset immediately so re-picking the SAME file still fires onChange.
    event.target.value = "";
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setMessage({
        type: "error",
        text: "Invalid file type. Only PNG and JPG are allowed.",
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMessage({
        type: "error",
        text: "File too large. Maximum size is 2MB.",
      });
      return;
    }

    clearPendingLogo();
    setPendingLogoFile(file);
    setPendingLogoPreview(URL.createObjectURL(file));
    // Choosing a file cancels a pending removal — the two are contradictory.
    setPendingLogoRemoval(false);
    setMessage({ type: "", text: "" });
  };

  // Stage a removal. The DELETE runs on save, for the same reason the upload
  // does: a merchant who clears the logo and then changes their mind should be
  // able to back out without having already lost it.
  const handleRemoveLogo = () => {
    if (pendingLogoFile) {
      // Backing out of a staged file, not removing the saved logo.
      clearPendingLogo();
      setMessage({ type: "", text: "" });
      return;
    }
    if (!logoUrl) return;
    setPendingLogoRemoval(true);
    setMessage({ type: "", text: "" });
  };

  const handleUndoLogoRemoval = () => {
    setPendingLogoRemoval(false);
    setMessage({ type: "", text: "" });
  };

  /**
   * Apply whatever the merchant staged for the logo.
   *
   * Runs as part of saving the panel rather than on its own, so the logo and
   * the text fields succeed or fail together as far as the merchant can tell.
   *
   * @returns {Promise<boolean>} false if the server refused, so the caller can
   *   stop before reporting success.
   */
  const commitPendingLogo = async () => {
    if (!hasPendingLogoChange()) return true;

    setIsUploadingLogo(true);
    try {
      if (pendingLogoRemoval) {
        const response = await fetchWithAuth(`${API_BASE_URL}/user-details/logo`, {
          method: "DELETE",
        });
        const data = await response.json();
        if (!data.success) {
          setMessage({ type: "error", text: data.message || "Failed to remove logo" });
          return false;
        }
        setLogoUrl("");
        setPendingLogoRemoval(false);
        return true;
      }

      const formData = new FormData();
      formData.append("logo", pendingLogoFile);

      const response = await fetchWithAuth(`${API_BASE_URL}/user-details/logo`, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      if (!data.success) {
        setMessage({ type: "error", text: data.message || "Failed to upload logo" });
        return false;
      }
      setLogoUrl(data.data.logoUrl);
      clearPendingLogo();
      return true;
    } catch (error) {
      setMessage({ type: "error", text: "Failed to save logo" });
      return false;
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const getInitials = () => {
    if (businessInfo.businessName) {
      return businessInfo.businessName
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "MP";
  };

  // Category-grouped settings rail so merchants scan by area instead of a flat
  // list. Auto-conversion now lives in the sidebar (its own page), not here.
  /* ── Profile completion ──────────────────────────────────────────────────
   *
   * Every entry maps to a field this page already loads and can already save,
   * so the figure is measured, not decorative, and each chip navigates to the
   * tab that owns the field.
   *
   * Three fields are deliberately NOT counted:
   *   - email     cannot be changed here (the input is disabled), so it could
   *               never be incomplete and would only pad the total;
   *   - businessType and country  are hydrated with defaults ('technology',
   *               'US') whenever the API returns nothing, so they are never
   *               empty and would inflate the percentage for a brand-new
   *               account that has in fact filled in nothing.
   */
  const completion = useMemo(() => {
    const items = [
      { key: "firstName", label: "First name", tab: "profile", done: !!profileInfo.firstName?.trim() },
      { key: "lastName", label: "Last name", tab: "profile", done: !!profileInfo.lastName?.trim() },
      { key: "phone", label: "Phone number", tab: "profile", done: !!profileInfo.phone?.trim() },
      { key: "businessName", label: "Business name", tab: "business", done: !!businessInfo.businessName?.trim() },
      { key: "website", label: "Website", tab: "business", done: !!businessInfo.website?.trim() },
      { key: "description", label: "Business description", tab: "business", done: !!businessInfo.description?.trim() },
      { key: "address", label: "Business address", tab: "business", done: !!businessInfo.address?.trim() },
      { key: "logo", label: "Business logo", tab: "business", done: !!logoUrl },
      { key: "wallet", label: "Payout wallet", tab: "wallet", done: !!walletInfo.ethAddress?.trim() },
    ];
    const doneCount = items.filter((it) => it.done).length;
    return {
      items,
      doneCount,
      total: items.length,
      // Floor, not round: 8 of 9 must never read as 100%.
      pct: Math.floor((doneCount / items.length) * 100),
      missing: items.filter((it) => !it.done),
    };
  }, [profileInfo, businessInfo, logoUrl, walletInfo]);

  const shownPct = useCountUp(completion.pct);

  // Ring geometry. r is in the SVG's own 120-unit box; the dash array is the
  // full circumference so dashoffset can express the remaining fraction.
  const RING_R = 54;
  const RING_C = 2 * Math.PI * RING_R;

  /* ── Rail sliding indicator ─────────────────────────────────────────────
   * Measured rather than derived from the index, because the groups carry
   * headings and margins, so item offsets are not a fixed stride. */
  const railRef = useRef(null);
  const [railSlider, setRailSlider] = useState(null);
  const [railPrimed, setRailPrimed] = useState(false);

  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;

    const measure = () => {
      const active = rail.querySelector(".settings-rail-item.active");
      if (!active) return;
      const next = { top: active.offsetTop, height: active.offsetHeight };
      setRailSlider((prev) =>
        prev && prev.top === next.top && prev.height === next.height ? prev : next,
      );
    };

    measure();
    // Fonts and icons can land a frame later and shift the rows.
    const raf = requestAnimationFrame(() => {
      measure();
      setRailPrimed(true);
    });
    const ro = new ResizeObserver(measure);
    ro.observe(rail);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [activeTab, isMobile]);

  const tabGroups = [
    {
      heading: "Account",
      items: [
        { id: "profile", label: "Profile Info", icon: User },
        { id: "business", label: "Business Info", icon: Building2 },
      ],
    },
    {
      heading: "Security & notifications",
      items: [
        { id: "security", label: "Security", icon: Shield },
        { id: "notifications", label: "Notifications", icon: Bell },
      ],
    },
    {
      heading: "Wallets & tokens",
      items: [
        { id: "wallet", label: "Wallet Info", icon: Wallet },
        { id: "Token", label: "Custom Token Support", icon: Coins },
      ],
    },
    ...(isSuperAdmin
      ? [{ heading: "Admin", items: [{ id: "payout", label: "Payout Settings", icon: SettingsIcon }] }]
      : []),
  ];

  // Title for the mobile detail-view back control.
  const activeTabLabel = tabGroups
    .flatMap((group) => group.items)
    .find((tab) => tab.id === activeTab)?.label;

  /* The single way to change section. Writes the id into ?tab= as well as into
   * state, which is what makes the header's Profile link reliable: if the rail
   * changed only state, the URL would go stale, and navigating to
   * ?tab=profile from a tab the URL still claimed was profile would not be a
   * location change at all -- the original no-op, just harder to spot.
   *
   * replace: true because switching tab is not a navigation the user expects
   * Back to undo one step at a time. */
  const selectTab = (tabId) => {
    setActiveTab(tabId);
    setErrors({});
    setTokenErrors({});
    setMessage({ type: "", text: "" });
    setSearchParams({ tab: tabId }, { replace: true });
  };

  // Applies a tab arriving from outside (the header menu, a bookmark, Back).
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (!requested || requested === activeTab) return;
    const permitted =
      SETTINGS_TAB_IDS.includes(requested) ||
      (isSuperAdmin && SETTINGS_ADMIN_TAB_IDS.includes(requested));
    if (!permitted) return;
    setActiveTab(requested);
    setErrors({});
    setTokenErrors({});
    setMessage({ type: "", text: "" });
    // On mobile the page is a drill-in list, so a deep link has to open the
    // section rather than leaving the user on the index.
    if (isMobile) setMobileSectionOpen(true);
  }, [searchParams, activeTab, isSuperAdmin, isMobile]);

  const openMobileSection = (tabId) => {
    selectTab(tabId);
    setMobileSectionOpen(true);
  };

  if (isLoading) {
    return (
      <div className="settings-page rd-page">
        <div className="rd-page-header">
          <div>
            <h1 className="rd-page-title">Settings</h1>
            <p className="rd-page-subtitle">
              Manage your account, business, team, and integrations.
            </p>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page rd-page">
      <div className="rd-page-header">
        <div>
          <h1 className="rd-page-title">Settings</h1>
          <p className="rd-page-subtitle">
            Manage your account, business, team, and integrations.
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`rd-message-banner ${message.type}`}>
          {message.type === "success" ? (
            <CheckCircle size={18} />
          ) : (
            <AlertCircle size={18} />
          )}
          <span>{message.text}</span>
          <button
            onClick={() => setMessage({ type: "", text: "" })}
            className="rd-message-close"
            aria-label="Dismiss message"
          >
            ×
          </button>
        </div>
      )}

      {isMobile && !mobileSectionOpen ? (
        <nav className="settings-mobile-list" aria-label="Settings sections">
          {tabGroups.map((group) => (
            <div key={group.heading} className="settings-mobile-section">
              <div className="settings-mobile-section-heading">
                {group.heading}
              </div>
              {group.items.map((tab) => (
                <button
                  key={tab.id}
                  className="settings-mobile-row"
                  onClick={() => openMobileSection(tab.id)}
                >
                  <tab.icon size={18} />
                  <span className="settings-mobile-row-label">{tab.label}</span>
                  <ChevronRight size={18} className="settings-mobile-row-chevron" />
                </button>
              ))}
            </div>
          ))}
        </nav>
      ) : (
      <div className="settings-layout">
        {!isMobile && (
          <nav className="settings-rail" aria-label="Settings sections" ref={railRef}>
            {railSlider && (
              <span
                className={`settings-rail__slider ${
                  railPrimed ? "settings-rail__slider--primed" : ""
                }`}
                style={{
                  transform: `translateY(${railSlider.top}px)`,
                  height: railSlider.height,
                }}
                aria-hidden="true"
              />
            )}
            {tabGroups.map((group) => (
              <div key={group.heading} className="settings-rail-group">
                <div className="settings-rail-heading">{group.heading}</div>
                {group.items.map((tab) => (
                  <button
                    key={tab.id}
                    className={`settings-rail-item ${activeTab === tab.id ? "active" : ""}`}
                    onClick={() => selectTab(tab.id)}
                  >
                    <tab.icon size={16} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
        )}

        <div className="settings-content">
          {isMobile && (
            <button
              type="button"
              className="settings-mobile-back"
              onClick={() => setMobileSectionOpen(false)}
            >
              <ChevronLeft size={20} />
              <span>{activeTabLabel || "Back"}</span>
            </button>
          )}
          {/*
            key={activeTab} is what makes the cross-fade run: without it React
            reuses this element across sections and the animation, having
            already finished, never restarts -- content would swap between two
            frames as before.
          */}
          <div className="settings-pane" key={activeTab}>
          {activeTab === "business" && (
            <>
              <div className="rd-card rd-card-pad settings-section">
                <div className="rd-section-header settings-card-head">
                  <div className="rd-icon-tile">
                    <Building2 size={20} />
                  </div>
                  <div>
                    <h2 className="rd-section-title">Business Information</h2>
                    <p className="rd-card-sub">
                      Used on invoices, receipts, and compliance documents.
                    </p>
                  </div>
                </div>

                <div className="settings-form">
                  <div className="rd-field">
                    <label className="rd-label">Business Logo</label>
                    <div className="logo-upload">
                      {/* What the merchant will have after saving, not what is
                          stored right now: a staged file wins, a staged removal
                          falls back to the initials. */}
                      <div className="logo-preview">
                        {pendingLogoPreview ? (
                          <img
                            src={pendingLogoPreview}
                            alt="Business logo preview"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        ) : logoUrl && !pendingLogoRemoval ? (
                          <img
                            src={`${API_BASE_URL.replace("/api", "")}${logoUrl}`}
                            alt="Business Logo"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                        ) : (
                          <span>{getInitials()}</span>
                        )}
                      </div>
                      <input
                        type="file"
                        id="logo-upload-input"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleLogoSelect}
                        style={{ display: "none" }}
                      />
                      <button
                        type="button"
                        className="rd-btn rd-btn-secondary rd-btn-sm"
                        onClick={() =>
                          document.getElementById("logo-upload-input").click()
                        }
                        disabled={isUploadingLogo || isSaving}
                      >
                        <Upload size={16} />
                        {logoUrl && !pendingLogoRemoval ? "Change Logo" : "Upload Logo"}
                      </button>

                      {pendingLogoRemoval ? (
                        <button
                          type="button"
                          className="rd-btn rd-btn-ghost rd-btn-sm logo-remove"
                          onClick={handleUndoLogoRemoval}
                          disabled={isUploadingLogo || isSaving}
                        >
                          Undo
                        </button>
                      ) : (
                        (pendingLogoFile || logoUrl) && (
                          <button
                            type="button"
                            className="rd-btn rd-btn-ghost rd-btn-sm logo-remove"
                            onClick={handleRemoveLogo}
                            disabled={isUploadingLogo || isSaving}
                          >
                            {pendingLogoFile ? "Cancel" : "Remove"}
                          </button>
                        )
                      )}

                      {/* Says plainly that nothing has been sent yet, so the
                          preview is not mistaken for a completed upload. */}
                      {pendingLogoFile ? (
                        <span className="rd-hint logo-staged">
                          Preview only — select Save changes to apply
                        </span>
                      ) : pendingLogoRemoval ? (
                        <span className="rd-hint logo-staged">
                          Will be removed when you save
                        </span>
                      ) : (
                        <span className="rd-hint">PNG, JPG up to 2MB</span>
                      )}
                    </div>
                  </div>

                  <div className="rd-grid-2col">
                    <div className="rd-field">
                      <label className="rd-label">Business Name</label>
                      <input
                        className={`rd-input ${errors.businessName ? "rd-input-error" : ""}`}
                        placeholder="Business Name"
                        type="text"
                        value={businessInfo.businessName}
                        aria-invalid={!!errors.businessName}
                        onChange={(e) => updateBusinessField("businessName", e.target.value)}
                      />
                      {errors.businessName && (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.businessName}
                        </span>
                      )}
                    </div>
                    <div className="rd-field">
                      <label className="rd-label">Business Type</label>
                      <select
                        className={`rd-select ${errors.businessType ? "rd-input-error" : ""}`}
                        value={businessInfo.businessType}
                        aria-invalid={!!errors.businessType}
                        onChange={(e) => updateBusinessField("businessType", e.target.value)}
                      >
                        <option value="technology">Technology</option>
                        <option value="ecommerce">E-Commerce</option>
                        <option value="saas">SaaS</option>
                        <option value="consulting">Consulting</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.businessType && (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.businessType}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rd-field">
                    <label className="rd-label">Website</label>
                    <input
                      className={`rd-input ${errors.website ? "rd-input-error" : ""}`}
                      type="url"
                      placeholder="Website"
                      value={businessInfo.website}
                      aria-invalid={!!errors.website}
                      onChange={(e) => updateBusinessField("website", e.target.value)}
                    />
                    {/* validateBusinessInfo always set this error, but nothing
                        rendered it -- a bad URL just made Save do nothing. */}
                    {errors.website && (
                      <span className="rd-error">
                        <AlertCircle size={14} />
                        {errors.website}
                      </span>
                    )}
                  </div>

                  <div className="rd-field">
                    <label className="rd-label">Business Description</label>
                    <textarea
                      className={`rd-textarea ${errors.description ? "rd-input-error" : ""}`}
                      placeholder="Business Description"
                      rows={3}
                      value={businessInfo.description}
                      aria-invalid={!!errors.description}
                      onChange={(e) => updateBusinessField("description", e.target.value)}
                    />
                    {errors.description && (
                      <span className="rd-error">
                        <AlertCircle size={14} />
                        {errors.description}
                      </span>
                    )}
                  </div>

                  <div className="rd-grid-2col">
                    <div className="rd-field">
                      <label className="rd-label">Country</label>
                      <select
                        className={`rd-select ${errors.country ? "rd-input-error" : ""}`}
                        value={businessInfo.country}
                        aria-invalid={!!errors.country}
                        onChange={(e) => {
                          const newCountry = e.target.value;
                          const config = COUNTRY_PHONE_CONFIG[newCountry];
                          // The dial code follows the country, and the number
                          // itself is cleared: a subscriber number is only
                          // valid for the country it was issued in, so keeping
                          // it under a new code would leave a number that
                          // cannot be dialled.
                          setBusinessInfo({
                            ...businessInfo,
                            country: newCountry,
                            phone: config ? config.code : businessInfo.phone,
                          });
                          if (errors.country) {
                            setErrors((prev) => {
                              const { country: _dropped, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      >
                        {Object.entries(COUNTRY_PHONE_CONFIG).map(
                          ([code, config]) => (
                            <option key={code} value={code}>
                              {config.label}
                            </option>
                          ),
                        )}
                      </select>
                      {errors.country && (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.country}
                        </span>
                      )}
                    </div>
                    <div className="rd-field">
                      <label className="rd-label">Phone Number</label>
                      {/* Same picker as Profile Info. The dial code is chosen
                          from a list with flags rather than typed, so a number
                          cannot be saved without one and the code cannot be
                          mistyped. */}
                      <PhoneInput
                        id="business-phone"
                        value={businessInfo.phone}
                        error={errors.phone}
                        onChange={(phone) => {
                          setBusinessInfo({ ...businessInfo, phone });
                          if (errors.phone) {
                            setErrors((prev) => {
                              const { phone: _dropped, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                        // The pairing runs both ways: picking a dial code
                        // moves the Country select to match, and clears the
                        // number for the same reason the select does.
                        onCountryChange={(iso) => {
                          const key = businessCountryForIso(iso);
                          setBusinessInfo((prev) => ({
                            ...prev,
                            country: key || prev.country,
                            phone: COUNTRY_PHONE_CONFIG[key]?.code
                              || `+${findCountry(iso).dial}`,
                          }));
                        }}
                      />
                      {errors.phone ? (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.phone}
                        </span>
                      ) : (
                        <span className="rd-hint">
                          Pick your country, then enter the number
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rd-field">
                    <label className="rd-label">Business Address</label>
                    <input
                      className={`rd-input ${errors.address ? "rd-input-error" : ""}`}
                      type="text"
                      placeholder="Business Address"
                      value={businessInfo.address}
                      aria-invalid={!!errors.address}
                      onChange={(e) => updateBusinessField("address", e.target.value)}
                    />
                    {errors.address && (
                      <span className="rd-error">
                        <AlertCircle size={14} />
                        {errors.address}
                      </span>
                    )}
                  </div>

                  {/* <div className="settings-kyb">
                    <div>
                      <div className="settings-kyb-title">
                        KYB verification status
                      </div>
                      <div className="rd-hint">
                        Required to switch to Live mode and accept real payments
                      </div>
                    </div>
                    <span className="rd-badge warn">Pending · 2 docs needed</span>
                  </div> */}
                </div>
              </div>
            </>
          )}

          {activeTab === "wallet" && (
            <div className="rd-card rd-card-pad settings-section">
              <div className="rd-section-header settings-card-head">
                <div className="rd-icon-tile">
                  <Wallet size={20} />
                </div>
                <div>
                  {isSuperAdmin ? (
                    <>
                      <h2 className="rd-section-title">System Wallet Addresses</h2>
                      <p className="rd-card-sub">
                        View system wallet addresses for gas tank and main
                        operations
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="rd-section-title">Wallet Configuration</h2>
                      <p className="rd-card-sub">
                        Configure your Ethereum wallet address for receiving
                        payments
                      </p>
                    </>
                  )}
                </div>
              </div>

              {isSuperAdmin ? (
                <div className="settings-form">
                  <div className="rd-field">
                    <label className="rd-label">Gas Tank Address</label>
                    <div className="wallet-input-row">
                      <input
                        type="text"
                        value={gasTankInfo.gasTankAddress}
                        disabled
                        className="rd-input rd-mono input-disabled"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Copy wallet address"
                        onClick={() =>
                          handleCopyAddress(
                            "gasTank",
                            gasTankInfo.gasTankAddress,
                          )
                        }
                        disabled={!gasTankInfo.gasTankAddress}
                      >
                        {copiedAddress === "gasTank" ? (
                          <Check size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                    <span className="rd-hint">
                      {copiedAddress === "gasTank"
                        ? "Copied!"
                        : "System wallet used for gas fees"}
                    </span>
                  </div>

                  <div className="rd-field">
                    <label className="rd-label">Main System Address</label>
                    <div className="wallet-input-row">
                      <input
                        type="text"
                        value={gasTankInfo.mainAddress}
                        disabled
                        className="rd-input rd-mono input-disabled"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label="Copy wallet address"
                        onClick={() =>
                          handleCopyAddress("main", gasTankInfo.mainAddress)
                        }
                        disabled={!gasTankInfo.mainAddress}
                      >
                        {copiedAddress === "main" ? (
                          <Check size={16} />
                        ) : (
                          <Copy size={16} />
                        )}
                      </button>
                    </div>
                    <span className="rd-hint">
                      {copiedAddress === "main"
                        ? "Copied!"
                        : "Main system wallet for operations"}
                    </span>
                  </div>

                  <div className="settings-note info">
                    <Shield size={18} />
                    <div>
                      <strong>System Wallets</strong>
                      <p>
                        These are read-only system wallet addresses. They are
                        managed by the private backend and cannot be modified
                        from this interface.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {pendingWalletRequest && (
                    <div className="settings-note warn pending-request-banner">
                      <div className="pending-request-icon">
                        <AlertCircle size={20} />
                      </div>
                      <div className="pending-request-content">
                        <h4>Wallet Change Request Pending</h4>
                        <p>
                          Your request to change wallet address is pending admin
                          approval.
                        </p>
                        <div className="pending-request-details">
                          <div className="detail-row">
                            <span className="detail-label">New Address:</span>
                            <span className="detail-value mono">
                              {pendingWalletRequest.newAddress}
                            </span>
                          </div>
                          {pendingWalletRequest.reason && (
                            <div className="detail-row">
                              <span className="detail-label">Reason:</span>
                              <span className="detail-value">
                                {pendingWalletRequest.reason}
                              </span>
                            </div>
                          )}
                          <div className="detail-row">
                            <span className="detail-label">Submitted:</span>
                            <span className="detail-value">
                              {new Date(
                                pendingWalletRequest.createdAt,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        </div>
                        <button
                          className="rd-btn rd-btn-secondary rd-btn-sm btn-cancel-request"
                          onClick={handleCancelWalletRequest}
                          disabled={isSaving}
                        >
                          Cancel Request
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="settings-form">
                    <div className="rd-field">
                      <label className="rd-label">Ethereum (ETH) Address</label>
                      <div className="wallet-input-row">
                        <input
                          type="text"
                          value={walletInfo.ethAddress}
                          onChange={(e) =>
                            setWalletInfo({
                              ...walletInfo,
                              ethAddress: e.target.value,
                            })
                          }
                          placeholder="0x..."
                          className={`rd-input rd-mono ${errors.ethAddress ? "rd-input-error" : ""}`}
                          disabled={!!pendingWalletRequest || isCheckingAddress}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="icon-btn"
                          aria-label="Copy wallet address"
                          onClick={() =>
                            handleCopyAddress("eth", walletInfo.ethAddress)
                          }
                          disabled={!walletInfo.ethAddress}
                        >
                          {copiedAddress === "eth" ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                      {isCheckingAddress && (
                        <span
                          className="info-text"
                          style={{
                            color: "var(--brand-600)",
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-2)",
                            marginTop: "var(--space-2)",
                          }}
                        >
                          <span
                            className="spinner"
                            style={{
                              width: "14px",
                              height: "14px",
                              border: "2px solid var(--brand-600)",
                              borderTopColor: "transparent",
                              borderRadius: "50%",
                              animation: "spin 0.6s linear infinite",
                            }}
                          ></span>
                          Verifying address type (EOA vs Contract)...
                        </span>
                      )}
                      {errors.ethAddress && (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.ethAddress}
                        </span>
                      )}
                      <span className="rd-hint">
                        Enter your Ethereum wallet address - only Externally
                        Owned Accounts (EOA) are allowed, not contract addresses
                      </span>
                    </div>

                    {originalWalletAddress &&
                      !pendingWalletRequest &&
                      walletInfo.ethAddress !== originalWalletAddress && (
                        <div className="rd-field">
                          <label className="rd-label">
                            Reason for Change
                            <span className="rd-optional">(optional)</span>
                          </label>
                          <textarea
                            className="rd-textarea"
                            value={walletChangeReason}
                            onChange={(e) =>
                              setWalletChangeReason(e.target.value)
                            }
                            placeholder="Please provide a reason for changing your wallet address..."
                            rows={3}
                          />
                          <span className="rd-hint">
                            This will help admin review your request faster.
                          </span>
                        </div>
                      )}

                    {originalWalletAddress && !pendingWalletRequest && (
                      <div className="settings-note info">
                        <Lock size={18} />
                        <div>
                          <strong>Approval Required</strong>
                          <p>
                            Changing your wallet address requires admin approval
                            for security purposes. Your current address will
                            remain active until the change is approved.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="settings-note warn">
                      <Shield size={18} />
                      <div>
                        <strong>Security Notice</strong>
                        <p>
                          Double-check your wallet address before saving.
                          Transactions to incorrect addresses cannot be
                          reversed.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "profile" && (
            <div className="settings-section settings-stack">
              {/*
                Completion hero. The portrait falls back to initials because the
                account has no avatar field -- user-details returns only
                businessInfo/profileInfo, and neither carries an image. The
                business logo is used when one has been uploaded, since that is
                the only real image the account owns; "Change" therefore sends
                the user to Business Info, which is where that upload lives.
              */}
              <div className="rd-card rd-card-pad settings-hero">
                <div className="settings-hero__avatar-wrap">
                  <svg className="settings-hero__ring" viewBox="0 0 120 120">
                    <defs>
                      <linearGradient id="settings-ring-gradient" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="var(--brand-500, #8b5cf6)" />
                        <stop offset="100%" stopColor="var(--brand-700, #6d28d9)" />
                      </linearGradient>
                    </defs>
                    <circle className="settings-hero__ring-track" cx="60" cy="60" r={RING_R} />
                    {/*
                      strokeDashoffset is the UNFILLED remainder, so 0% is a
                      full offset and 100% is none. It reads completion.pct,
                      not the tweened figure, so the CSS transition owns the
                      sweep and the ring never depends on frame timing.
                    */}
                    <circle
                      className="settings-hero__ring-fill"
                      cx="60"
                      cy="60"
                      r={RING_R}
                      strokeDasharray={RING_C}
                      strokeDashoffset={RING_C * (1 - completion.pct / 100)}
                    />
                  </svg>

                  <div className="settings-hero__avatar">
                    {logoUrl ? (
                      <img
                        src={`${API_BASE_URL.replace("/api", "")}${logoUrl}`}
                        alt=""
                      />
                    ) : (
                      <span className="settings-hero__initials">
                        {initialsOf(profileInfo.firstName, profileInfo.lastName, profileInfo.email)}
                      </span>
                    )}
                  </div>

                  <span className="settings-hero__pct">{shownPct}%</span>
                </div>

                <div className="settings-hero__body">
                  <h2 className="settings-hero__name">
                    {[profileInfo.firstName, profileInfo.lastName]
                      .filter(Boolean)
                      .join(" ") || "Your profile"}
                  </h2>
                  <span className="settings-hero__email">{profileInfo.email}</span>

                  <p className="settings-hero__progress-label">
                    {completion.doneCount} of {completion.total} details completed
                  </p>

                  {completion.missing.length > 0 ? (
                    <ul className="settings-hero__todo">
                      {completion.missing.map((item) => (
                        <li key={item.key}>
                          <button
                            type="button"
                            className="settings-hero__chip"
                            onClick={() => {
                              if (isMobile) {
                                openMobileSection(item.tab);
                              } else {
                                selectTab(item.tab);
                              }
                            }}
                          >
                            <Circle size={13} />
                            {item.label}
                            <ChevronRight size={13} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="settings-hero__done">
                      <CheckCircle size={16} />
                      Your profile is complete
                    </span>
                  )}
                </div>
              </div>

              <div className="rd-card rd-card-pad">
                <div className="rd-section-header settings-card-head">
                  <div className="rd-icon-tile">
                    <User size={20} />
                  </div>
                  <div>
                    <h2 className="rd-section-title">Profile</h2>
                    <p className="rd-card-sub">
                      This is how you'll appear inside MaxelPay
                    </p>
                    {/* TODO: KYC status not available from current profile data
                        (user-details API returns only businessInfo/profileInfo,
                        and the auth user object has no verification/KYC field).
                        Render a status badge here once a field such as
                        kycStatus/verificationStatus/is_verified is available. */}
                  </div>
                </div>

                <div className="settings-form">
                  <div className="rd-grid-2col">
                    <div className="rd-field">
                      <label className="rd-label">First Name</label>
                      <input
                        className={`rd-input ${errors.firstName ? "rd-input-error" : ""}`}
                        type="text"
                        value={profileInfo.firstName}
                        onChange={(e) =>
                          setProfileInfo({
                            ...profileInfo,
                            firstName: e.target.value,
                          })
                        }
                      />
                      {errors.firstName && (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.firstName}
                        </span>
                      )}
                    </div>
                    <div className="rd-field">
                      <label className="rd-label">Last Name</label>
                      <input
                        className={`rd-input ${errors.lastName ? "rd-input-error" : ""}`}
                        type="text"
                        value={profileInfo.lastName}
                        onChange={(e) =>
                          setProfileInfo({
                            ...profileInfo,
                            lastName: e.target.value,
                          })
                        }
                      />
                      {errors.lastName && (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.lastName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="rd-grid-2col">
                    <div className="rd-field">
                      <label className="rd-label">Email Address</label>
                      <input
                        className="rd-input input-disabled"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={profileInfo.email}
                        disabled
                      />
                      <span className="rd-hint">
                        Used for sign-in and account notifications
                      </span>
                    </div>
                    <div className="rd-field">
                      <label className="rd-label">Phone Number</label>
                      <PhoneInput
                        id="phone"
                        value={profileInfo.phone}
                        error={errors.phone}
                        onChange={(phone) => {
                          setProfileInfo({ ...profileInfo, phone });
                          if (errors.phone) {
                            setErrors((prev) => {
                              const { phone: _dropped, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                      />
                      {errors.phone ? (
                        <span className="rd-error">
                          <AlertCircle size={14} />
                          {errors.phone}
                        </span>
                      ) : (
                        <span className="rd-hint">
                          Pick your country, then enter the number
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rd-card rd-card-pad">
                <div className="rd-section-header settings-card-head">
                  <div className="rd-icon-tile">
                    <Lock size={20} />
                  </div>
                  <div>
                    <h2 className="rd-section-title">Change Password</h2>
                    <p className="rd-card-sub">Update your account password</p>
                  </div>
                </div>

                <div className="settings-form">
                <div className="rd-field">
                  <label className="rd-label">Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordInfo.currentPassword}
                      onChange={(e) =>
                        setPasswordInfo({
                          ...passwordInfo,
                          currentPassword: e.target.value,
                        })
                      }
                      className={`rd-input ${errors.currentPassword ? "rd-input-error" : ""}`}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      aria-label={
                        showPasswords.current ? "Hide password" : "Show password"
                      }
                      onClick={() =>
                        setShowPasswords({
                          ...showPasswords,
                          current: !showPasswords.current,
                        })
                      }
                    >
                      {showPasswords.current ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <span className="rd-error">
                      <AlertCircle size={14} />
                      {errors.currentPassword}
                    </span>
                  )}
                </div>

                <div className="rd-grid-2col">
                  <div className="rd-field">
                    <label className="rd-label">New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordInfo.newPassword}
                        onChange={(e) =>
                          setPasswordInfo({
                            ...passwordInfo,
                            newPassword: e.target.value,
                          })
                        }
                        className={`rd-input ${errors.newPassword ? "rd-input-error" : ""}`}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        aria-label={
                          showPasswords.new ? "Hide password" : "Show password"
                        }
                        onClick={() =>
                          setShowPasswords({
                            ...showPasswords,
                            new: !showPasswords.new,
                          })
                        }
                      >
                        {showPasswords.new ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    {errors.newPassword && (
                      <span className="rd-error">
                        <AlertCircle size={14} />
                        {errors.newPassword}
                      </span>
                    )}
                    <span className="rd-hint">
                      Min 8 characters with uppercase, lowercase, and number
                    </span>
                  </div>
                  <div className="rd-field">
                    <label className="rd-label">Confirm New Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordInfo.confirmPassword}
                        onChange={(e) =>
                          setPasswordInfo({
                            ...passwordInfo,
                            confirmPassword: e.target.value,
                          })
                        }
                        className={`rd-input ${errors.confirmPassword ? "rd-input-error" : ""}`}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        aria-label={
                          showPasswords.confirm
                            ? "Hide password"
                            : "Show password"
                        }
                        onClick={() =>
                          setShowPasswords({
                            ...showPasswords,
                            confirm: !showPasswords.confirm,
                          })
                        }
                      >
                        {showPasswords.confirm ? (
                          <EyeOff size={18} />
                        ) : (
                          <Eye size={18} />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <span className="rd-error">
                        <AlertCircle size={14} />
                        {errors.confirmPassword}
                      </span>
                    )}
                  </div>
                </div>

                <div className="settings-form-actions">
                  <button
                    className="rd-btn rd-btn-primary"
                    onClick={handleChangePassword}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <div className="loading-spinner small" />
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Lock size={16} />
                        Change Password
                      </>
                    )}
                  </button>
                </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === "Token" && (
            <div className="rd-card rd-card-pad settings-section">
              <div className="rd-section-header settings-card-head">
                <div className="rd-icon-tile">
                  <Coins size={20} />
                </div>
                <div>
                  <h2 className="rd-section-title">
                    Request Custom Token Support
                  </h2>
                  <p className="rd-card-sub">
                    Submit token details to enable payments for your custom
                    token.
                  </p>
                </div>
              </div>

              <div className="settings-form">
                <div className="rd-field">
                  <label className="rd-label">Token Address</label>
                  <input
                    className={`rd-input rd-mono ${tokenErrors.contractAddress ? "rd-input-error" : ""}`}
                    type="text"
                    placeholder="0x..."
                    value={customToken.contractAddress}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomToken({ ...customToken, contractAddress: val, decimals: "" });
                      setResolvedTokenMeta({ name: "", symbol: "" });
                      if (val && !/^0x[a-fA-F0-9]{40}$/.test(val)) {
                        setTokenErrors(prev => ({ ...prev, contractAddress: "Invalid token address" }));
                      } else {
                        setTokenErrors(prev => ({ ...prev, contractAddress: "" }));
                      }
                    }}
                  />
                  {tokenErrors.contractAddress && (
                    <span className="rd-error">
                      <AlertCircle size={14} />
                      {tokenErrors.contractAddress}
                    </span>
                  )}
                </div>

                <div className="rd-grid-2col">
                  <div className="rd-field">
                    <label className="rd-label">Chain ID</label>
                    <input
                      className={`rd-input rd-mono ${tokenErrors.chainId ? "rd-input-error" : ""}`}
                      type="number"
                      placeholder="e.g. 1, 56, 137"
                      value={customToken.chainId}
                      onChange={(e) => {
                        setCustomToken({
                          ...customToken,
                          chainId: e.target.value,
                          decimals: "",
                        });
                        setResolvedTokenMeta({ name: "", symbol: "" });
                      }}
                    />
                    {tokenErrors.chainId && (
                      <span className="rd-error">
                        <AlertCircle size={14} />
                        {tokenErrors.chainId}
                      </span>
                    )}
                  </div>
                  <div className="rd-field">
                    <label className="rd-label">Decimals</label>
                    <input
                      className="rd-input rd-mono input-disabled"
                      type="number"
                      placeholder={isResolvingToken ? "Detecting…" : "Auto-detected"}
                      value={customToken.decimals}
                      readOnly
                    />
                    <span className="rd-hint">
                      {isResolvingToken
                        ? "Reading from the token contract…"
                        : "Read automatically from the token contract."}
                    </span>
                  </div>
                </div>

                {(resolvedTokenMeta.name || resolvedTokenMeta.symbol) && (
                  <div className="rd-grid-2col">
                    <div className="rd-field">
                      <label className="rd-label">Token Name</label>
                      <input
                        className="rd-input input-disabled"
                        type="text"
                        value={resolvedTokenMeta.name}
                        readOnly
                      />
                    </div>
                    <div className="rd-field">
                      <label className="rd-label">Token Symbol</label>
                      <input
                        className="rd-input rd-mono input-disabled"
                        type="text"
                        value={resolvedTokenMeta.symbol}
                        readOnly
                      />
                    </div>
                  </div>
                )}

                <div className="rd-field">
                  <label className="rd-label">
                    Additional Notes<span className="rd-optional">(optional)</span>
                  </label>
                  <textarea
                    className="rd-textarea"
                    placeholder="Any additional information"
                    value={customToken.notes}
                    onChange={(e) =>
                      setCustomToken({
                        ...customToken,
                        notes: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "payout" && isSuperAdmin && (
            <div className="settings-section">
              <PayoutSettingsTab />
            </div>
          )}

          {activeTab === "security" && <SecurityTab />}

          {activeTab === "notifications" && (
            <div className="settings-section">
              <NotificationsTab />
            </div>
          )}

          {activeTab !== "payout" &&
            activeTab !== "security" &&
            activeTab !== "notifications" &&
            !(activeTab === "wallet" && isSuperAdmin) && (
              <div className="settings-footer">
                <button
                  className="rd-btn rd-btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <div className="loading-spinner small" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Settings;
